import React, { useState } from "react";
import { RiMessage2Line, RiSettings3Line } from "react-icons/ri";
import ChatHistoryList from "./ChatHistoryList";
import LLMSettingsSection from "./LLMSettingsSection";
import PanelShell, { PANEL_PADDING_X } from "./PanelShell";

function LeftPanel({
  activeTab = "conversations",
  onTabChange,
  scrollAreaStyle,
  API_URL, user,
  activeConversationId, onSelectConversation, onDeleteConversation, onRenameConversation,
  isDeleting, isRenaming,
  onResetChat, isResetting,
  historyRefreshTrigger, loadingConversationIds, pendingConversations, onHistoriesFetched,
  justCompletedIds,
  swarmSessions, pendingSwarmSessions, loadingSwarmSessionIds,
  onSelectSwarmSession, onDeleteSwarmSession, onRenameSwarmSession,
  isDeletingSwarm, isRenamingSwarm, activeSwarmSessionId, swarmSessionsLoading,
  onModeChange, activeMode,
  workflowRuns = [], runningWorkflowRunIds, workflowList = [],
  onRenameWorkflowRun, onDeleteWorkflowRun, onSelectWorkflowRun, activeWorkflowRunId = null,
  modelSettings = {},
  theme, toggleTheme, selectedApis, setSelectedApis,
  refreshHistories, resetMainPanel, refreshModelSettings,
  onClose,
  collapsed = false,
  onToggleCollapse,
}) {
  const [activeTabState, setActiveTabState] = useState(activeTab);
  const tab = onTabChange ? activeTab : activeTabState;
  const setTab = (t) => { onTabChange ? onTabChange(t) : setActiveTabState(t); };

  const tabBtnStyle = (t) => ({
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
    padding: "6px 0", border: "none",
    borderBottom: `2px solid ${tab === t ? "var(--primary-color, #636cff)" : "transparent"}`,
    background: "transparent",
    color: tab === t ? "var(--primary-color, #636cff)" : "var(--text-secondary, #888)",
    fontWeight: tab === t ? "600" : "400",
    fontSize: "0.82rem", cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap",
  });

  return (
    <PanelShell
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      onClose={onClose}
      theme={theme}
      toggleTheme={toggleTheme}
      selectedApis={selectedApis}
      setSelectedApis={setSelectedApis}
      refreshHistories={refreshHistories}
      resetMainPanel={resetMainPanel}
      refreshModelSettings={refreshModelSettings}
      API_URL={API_URL}
    >
      {/* ── Tab bar ── */}
      <div style={{
        display: "flex", flexShrink: 0,
        borderBottom: "1px solid var(--panel-border)",
        marginBottom: "0.5rem",
        padding: `0 ${PANEL_PADDING_X}`,
      }}>
        <button style={tabBtnStyle("conversations")} onClick={() => setTab("conversations")}>
          <RiMessage2Line size={14} /> Stacks
        </button>
        <button style={tabBtnStyle("settings")} onClick={() => setTab("settings")}>
          <RiSettings3Line size={14} /> Configurations
        </button>
      </div>

      {/* ── Stacks tab — flex column so the list scrolls and "New Chat"
           sits below the scroll area (always visible, never duplicated) ── */}
      <div style={{
        flex: 1, minHeight: 0,
        display: tab === "conversations" ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Scrollable history area */}
        <div style={{
          flex: 1, overflowY: "auto", minHeight: 0,
          padding: `0 ${PANEL_PADDING_X}`,
        }}>
          <ChatHistoryList
            API_URL={API_URL} user={user}
            activeConversationId={activeConversationId}
            onSelectConversation={onSelectConversation}
            onDeleteConversation={onDeleteConversation}
            onRenameConversation={onRenameConversation}
            isDeleting={isDeleting} isRenaming={isRenaming}
            refreshTrigger={historyRefreshTrigger}
            loadingConversationIds={loadingConversationIds}
            pendingConversations={pendingConversations}
            onHistoriesFetched={onHistoriesFetched}
            justCompletedIds={justCompletedIds}
            swarmSessions={swarmSessions}
            pendingSwarmSessions={pendingSwarmSessions}
            loadingSwarmSessionIds={loadingSwarmSessionIds}
            onSelectSwarmSession={onSelectSwarmSession}
            onDeleteSwarmSession={onDeleteSwarmSession}
            onRenameSwarmSession={onRenameSwarmSession}
            isDeletingSwarm={isDeletingSwarm} isRenamingSwarm={isRenamingSwarm}
            activeSwarmSessionId={activeSwarmSessionId}
            swarmSessionsLoading={swarmSessionsLoading}
            onModeChange={onModeChange} activeMode={activeMode}
            workflowRuns={workflowRuns} runningWorkflowRunIds={runningWorkflowRunIds} workflowList={workflowList}
            onRenameWorkflowRun={onRenameWorkflowRun}
            onDeleteWorkflowRun={onDeleteWorkflowRun}
            onSelectWorkflowRun={onSelectWorkflowRun}
            activeWorkflowRunId={activeWorkflowRunId}
          />
        </div>
        {/* Non-scrolling "New Chat" footer — always visible regardless of list height */}
        <div style={{ flexShrink: 0, padding: `0.5rem ${PANEL_PADDING_X} 1rem` }}>
          <button
            style={{
              width: "50%", padding: "10px",
              backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "6px",
              fontSize: "14px", fontWeight: "500",
              cursor: isResetting ? "not-allowed" : "pointer",
              opacity: isResetting ? 0.6 : 1, transition: "all 0.2s ease",
              display: "block", marginLeft: "auto", marginRight: "auto",
            }}
            onClick={onResetChat}
            disabled={isResetting}
            onMouseEnter={(e) => { if (!isResetting) e.target.style.backgroundColor = "#7d1126"; }}
            onMouseLeave={(e) => { if (!isResetting) e.target.style.backgroundColor = "#c82333"; }}
          >
            {isResetting ? "Resetting..." : "New Chat"}
          </button>
        </div>
      </div>

      {/* ── Configurations tab ── */}
      <div style={{
        flex: 1, minHeight: 0,
        display: tab === "settings" ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        <div style={{
          flex: 1, overflowY: "auto", minHeight: 0,
          padding: `0 ${PANEL_PADDING_X} 1rem`,
        }}>
          <LLMSettingsSection {...modelSettings} />
        </div>
      </div>
    </PanelShell>
  );
}

export default React.memo(LeftPanel);
